import {
  type Dispatch,
  type FunctionComponent,
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useReducer,
} from 'react';

import type { ReservationCapacityResponse } from '#modules/reservation/api-types';

export interface ReservationExtraItem {
  readonly id: string;
  readonly title?: string;
  readonly price: number;
  readonly quantity: number; // 1+
  readonly guest: number | 'all';
}

export interface ReservationTreatmentItem {
  readonly baseTreatmentId: string;
  readonly baseTreatmentName: string;
  readonly optionId: string;
  readonly optionName: string;
  readonly customerPosition: string;
  readonly price: number;
}

export interface ArrangementOptionChoice {
  readonly id: string;
  readonly name: string;
  readonly treatmentName?: string | null;
}

export interface ArrangementOptionDefinition {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  readonly optional: boolean;
  readonly options: readonly ArrangementOptionChoice[];
}

export interface ArrangementDealDefinition {
  readonly id: string;
  readonly name: string;
  readonly information?: string | null;
  readonly price: number;
  readonly hasEntrance: boolean;
  readonly entranceValue: number;
  readonly bookableDays: readonly number[];
  readonly availableFrom?: string | null;
  readonly availableUntil?: string | null;
  readonly treatmentCategoryIds: readonly string[];
  readonly timeRange?: {
    readonly arrival: {
      readonly min?: string | null;
      readonly max?: string | null;
    };
    readonly departure: {
      readonly min?: string | null;
      readonly max?: string | null;
    };
  } | null;
  readonly optionGroups: readonly ArrangementOptionDefinition[];
}

export interface ArrangementOptionSelection {
  readonly optionGroupId: string;
  readonly optionId: string;
  readonly optionName: string;
  readonly optionValue: number;
}

export interface ArrangementSelection {
  readonly customerPosition: number;
  readonly options: readonly ArrangementOptionSelection[];
}

export interface ReservationPackageUpsell {
  readonly customerPosition: number;
  readonly deal: ArrangementDealDefinition;
  readonly options: readonly ArrangementOptionSelection[];
}

export interface CustomerDetailsState {
  readonly firstName: string;
  readonly tussenvoegsel?: string;
  readonly lastName: string;
  readonly email: string;
  readonly phone: string;
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
}

export interface ReservationState {
  readonly reservationDate: string | null; // ISO
  readonly persons: number | null;
  readonly entranceId: string | null;
  readonly entranceDeal: ArrangementDealDefinition | null;
  readonly arrivalTime: string | null; // HH:mm
  readonly departureTime: string | null; // HH:mm
  readonly capacity: ReservationCapacityResponse | null;
  readonly extras: readonly ReservationExtraItem[];
  readonly treatments: readonly ReservationTreatmentItem[];
  readonly arrangementDeal: ArrangementDealDefinition | null;
  readonly arrangementSelections: readonly ArrangementSelection[];
  readonly packageUpsells: readonly ReservationPackageUpsell[];
  readonly customer: CustomerDetailsState;
  readonly newCustomer: boolean;
  readonly wantsNewsletter: boolean;
  readonly acceptedTerms18Plus: boolean;
  readonly customerNote?: string;
  readonly accountToken: string | null;
  readonly accountEmail: string | null;
}

const initialCustomer: CustomerDetailsState = {
  firstName: '',
  tussenvoegsel: '',
  lastName: '',
  email: '',
  phone: '',
  street: '',
  postalCode: '',
  city: '',
};

export const initialReservationState: ReservationState = {
  reservationDate: null,
  persons: null,
  entranceId: null,
  entranceDeal: null,
  arrivalTime: null,
  departureTime: null,
  capacity: null,
  extras: [],
  treatments: [],
  arrangementDeal: null,
  arrangementSelections: [],
  packageUpsells: [],
  customer: initialCustomer,
  newCustomer: false,
  wantsNewsletter: false,
  acceptedTerms18Plus: false,
  customerNote: '',
  accountToken: null,
  accountEmail: null,
};

type Action =
  | { type: 'setReservationDate'; payload: string | null }
  | { type: 'setPersons'; payload: number | null }
  | { type: 'setEntranceId'; payload: string | null }
  | { type: 'setEntranceSelection'; payload: { id: string; deal: ArrangementDealDefinition } }
  | { type: 'setArrivalTime'; payload: string | null }
  | { type: 'setDepartureTime'; payload: string | null }
  | { type: 'setCapacity'; payload: ReservationCapacityResponse | null }
  | { type: 'setExtras'; payload: readonly ReservationExtraItem[] }
  | { type: 'upsertExtra'; payload: ReservationExtraItem }
  | { type: 'removeExtra'; payload: { id: string; guest: number | 'all' } }
  | { type: 'setTreatments'; payload: readonly ReservationTreatmentItem[] }
  | { type: 'setArrangementDeal'; payload: ArrangementDealDefinition | null }
  | { type: 'setArrangementSelections'; payload: readonly ArrangementSelection[] }
  | { type: 'setArrangementSelection'; payload: ArrangementSelection }
  | { type: 'setPackageUpsells'; payload: readonly ReservationPackageUpsell[] }
  | { type: 'setCustomer'; payload: Partial<CustomerDetailsState> }
  | { type: 'setNewCustomer'; payload: boolean }
  | { type: 'setWantsNewsletter'; payload: boolean }
  | { type: 'setAcceptedTerms18Plus'; payload: boolean }
  | { type: 'setCustomerNote'; payload: string }
  | { type: 'setAccountToken'; payload: { token: string | null; email?: string | null } }
  | { type: 'reset' };

function reservationReducer(state: ReservationState, action: Action): ReservationState {
  switch (action.type) {
    case 'setReservationDate':
      return {
        ...state,
        reservationDate: action.payload,
        packageUpsells: state.reservationDate === action.payload ? state.packageUpsells : [],
      };
    case 'setPersons':
      return {
        ...state,
        persons: action.payload,
        packageUpsells: state.packageUpsells.filter((entry) => entry.customerPosition <= (action.payload ?? 0)),
      };
    case 'setEntranceId':
      return {
        ...state,
        entranceId: action.payload,
        packageUpsells: state.entranceId === action.payload ? state.packageUpsells : [],
        entranceDeal: action.payload && state.entranceDeal?.id === action.payload ? state.entranceDeal : null,
      };
    case 'setEntranceSelection':
      return {
        ...state,
        entranceId: action.payload.id,
        entranceDeal: action.payload.deal,
        packageUpsells: state.entranceId === action.payload.id ? state.packageUpsells : [],
      };
    case 'setArrivalTime':
      return { ...state, arrivalTime: action.payload };
    case 'setDepartureTime':
      return { ...state, departureTime: action.payload };
    case 'setCapacity':
      return { ...state, capacity: action.payload };
    case 'setExtras':
      return { ...state, extras: [...action.payload] };
    case 'upsertExtra': {
      const next = state.extras.filter((x) => !(x.id === action.payload.id && x.guest === action.payload.guest));
      return { ...state, extras: [...next, action.payload] };
    }
    case 'removeExtra': {
      const next = state.extras.filter((x) => !(x.id === action.payload.id && x.guest === action.payload.guest));
      return { ...state, extras: next };
    }
    case 'setTreatments':
      return { ...state, treatments: [...action.payload] };
    case 'setArrangementDeal': {
      const isSameDeal =
        state.arrangementDeal?.id && action.payload?.id && state.arrangementDeal.id === action.payload.id;
      return {
        ...state,
        arrangementDeal: action.payload,
        arrangementSelections: isSameDeal ? state.arrangementSelections : [],
        packageUpsells: state.arrangementDeal?.id === action.payload?.id ? state.packageUpsells : [],
      };
    }
    case 'setArrangementSelections':
      return { ...state, arrangementSelections: [...action.payload] };
    case 'setArrangementSelection': {
      const next = state.arrangementSelections.filter(
        (selection) => selection.customerPosition !== action.payload.customerPosition,
      );
      return { ...state, arrangementSelections: [...next, action.payload] };
    }
    case 'setPackageUpsells': {
      const byGuest = new Map<number, ReservationPackageUpsell>();
      for (const entry of action.payload) {
        if (
          Number.isInteger(entry.customerPosition) &&
          entry.customerPosition >= 1 &&
          entry.customerPosition <= (state.persons ?? 0)
        ) {
          byGuest.set(entry.customerPosition, entry);
        }
      }
      const packageUpsells = [...byGuest.values()].sort((a, b) => a.customerPosition - b.customerPosition);
      if (
        packageUpsells.length === state.packageUpsells.length &&
        packageUpsells.every((entry, index) => entry === state.packageUpsells[index])
      ) {
        return state;
      }
      return { ...state, packageUpsells };
    }
    case 'setCustomer':
      return { ...state, customer: { ...state.customer, ...action.payload } };
    case 'setNewCustomer':
      return { ...state, newCustomer: action.payload };
    case 'setWantsNewsletter':
      return { ...state, wantsNewsletter: action.payload };
    case 'setAcceptedTerms18Plus':
      return { ...state, acceptedTerms18Plus: action.payload };
    case 'setCustomerNote':
      return { ...state, customerNote: action.payload };
    case 'setAccountToken':
      return { ...state, accountToken: action.payload.token, accountEmail: action.payload.email ?? state.accountEmail };
    case 'reset':
      return initialReservationState;
    default:
      return state;
  }
}

interface ReservationContextValue {
  readonly state: ReservationState;
  readonly dispatch: Dispatch<Action>;
}

const ReservationContext = createContext<ReservationContextValue | null>(null);

export interface ReservationProviderProps {
  readonly children?: ReactNode;
  readonly initialState?: ReservationState;
}

export const ReservationProvider: FunctionComponent<ReservationProviderProps> = (props) => {
  const { children, initialState } = props;
  const [state, dispatch] = useReducer(reservationReducer, initialState ?? initialReservationState);
  const value = useMemo<ReservationContextValue>(() => ({ state, dispatch }), [state]);
  return <ReservationContext.Provider value={value}>{children}</ReservationContext.Provider>;
};

export const useReservation = (): ReservationContextValue => {
  const ctx = useContext(ReservationContext);
  if (!ctx) {
    throw new Error('useReservation must be used within a ReservationProvider');
  }
  return ctx;
};

// Selectors / view models kept headless for UI components to consume
export interface OverviewGuestLineItem {
  readonly label: string;
  readonly price: number;
  readonly isIncluded?: boolean;
}

export interface OverviewViewModel {
  readonly dateLabel: string | null;
  readonly timeRangeLabel: string | null;
  readonly guests: readonly {
    readonly guestLabel: string;
    readonly total: number;
    readonly items: readonly OverviewGuestLineItem[];
  }[];
  readonly grandTotal: number;
  readonly customerSummary: {
    readonly name: string;
    readonly email: string;
    readonly phone: string;
  };
}

export function formatCurrencyNl(value: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

// Flatten reservation selections into a read-only summary for the overview screen.
export function buildOverviewViewModel(
  state: ReservationState,
  entrancePricePerPerson?: number,
  entranceLabel?: string,
): OverviewViewModel {
  const dateLabel = state.reservationDate
    ? new Intl.DateTimeFormat('nl-NL', { dateStyle: 'full' }).format(new Date(state.reservationDate))
    : null;
  const timeRangeLabel =
    state.arrivalTime && state.departureTime ? `${state.arrivalTime} - ${state.departureTime}` : null;

  const persons = state.persons ?? 0;

  const arrangement = state.arrangementDeal;
  const arrangementSelections = state.arrangementSelections;

  const getSelectionForGuest = (guestIndex: number) => {
    return arrangementSelections.find((selection) => selection.customerPosition === guestIndex) ?? null;
  };

  // group extras per guest index; 'all' applies to each guest
  const guestIndexes = Array.from({ length: persons }, (_, i) => i + 1);

  const guests = guestIndexes.map((guestIndex) => {
    const items: OverviewGuestLineItem[] = [];
    const entrance =
      typeof entrancePricePerPerson === 'number' && entranceLabel
        ? { label: entranceLabel, price: entrancePricePerPerson }
        : null;
    if (entrance) {
      items.push({ label: entrance.label, price: entrance.price });
    }

    if (arrangement) {
      items.push({
        label: arrangement.name,
        price: arrangement.price,
      });

      const selection = getSelectionForGuest(guestIndex);
      selection?.options.forEach((option) => {
        items.push({ label: option.optionName, price: 0, isIncluded: true });
      });
    }

    const packageUpsell = state.packageUpsells.find((entry) => entry.customerPosition === guestIndex);
    if (packageUpsell) {
      items.push({ label: packageUpsell.deal.name, price: packageUpsell.deal.price });
      packageUpsell.options.forEach((option) => {
        items.push({ label: option.optionName, price: 0, isIncluded: true });
      });
    }

    state.extras.forEach((e) => {
      const applies = e.guest === 'all' || e.guest === guestIndex;
      if (!applies) return;
      items.push({ label: e.title ?? e.id, price: e.price * e.quantity });
    });

    state.treatments
      .filter((treatment) => treatment.customerPosition === String(guestIndex))
      .forEach((treatment) => {
        const label = treatment.optionName
          ? `${treatment.baseTreatmentName} – ${treatment.optionName}`
          : treatment.baseTreatmentName;
        items.push({ label, price: treatment.price });
      });

    const total = items.reduce((sum, it) => sum + it.price, 0);
    return { guestLabel: `Gast ${guestIndex}`, total, items } as const;
  });

  const grandTotal = guests.reduce((sum, g) => sum + g.total, 0);

  const customerSummary = {
    name: [state.customer.firstName, state.customer.tussenvoegsel, state.customer.lastName]
      .filter(Boolean)
      .join(' ')
      .trim(),
    email: state.customer.email,
    phone: state.customer.phone,
  } as const;

  return { dateLabel, timeRangeLabel, guests, grandTotal, customerSummary };
}
